import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, FlaskConical, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errMsg } from "../ui";
import { fmtTime, money } from "./meta";

const WHY = { unchanged: "it already had the right amount, so nothing was written", special: "it's a special billing customer, never written",
  no_subscription: "no active subscription has this reference", no_usage_product: "its subscription has no usage product",
  no_calls: "it has no calls in the open cycle yet" };

function LastTest({ t }) {
  const ok = t.status === "updated";
  return (
    <div className={`mt-1 flex items-start gap-1.5 text-[11px] ${ok ? "text-emerald-700" : "text-amber-700"}`} data-testid="uc-live-test-result">
      {ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
      <span>{ok ? <>Wrote <b>{money(t.amount)}</b> to <b>{t.domain}</b>{t.customer ? ` (${t.customer})` : ""} — check it in Stax Bill.</>
        : <><b>{t.domain}</b>: not written — {t.message || WHY[t.status] || t.status}. Pick a domain marked “Would change”.</>}
        {" "}{fmtTime(t.at)} by {t.by} · <Link to={`runs/${t.run_id}`} className="underline" data-testid="uc-live-test-run">open the run</Link></span>
    </div>
  );
}

/** Step 1 before going live: write ONE domain's usage amount to Stax Bill and check it there. Super admins only. */
export function StaxLiveTest({ ov, reload }) {
  const t = ov.config.live_test;
  const rid = ov.latest_usage?.id;
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState([]);
  useEffect(() => {
    if (!open || !rid) return;
    api.get(`/usage-charges/runs/${rid}/domains`, { params: { status: "would_update", sort: "amount", page_size: 200 } })
      .then(({ data }) => setChoices(data.rows)).catch(() => setChoices([]));
  }, [open, rid]);
  const start = async () => {
    setBusy(true);
    try {
      await api.post("/usage-charges/live-test", { domain: domain.trim(), confirm: ok });
      toast.success(`Live test started for ${domain.trim()} — the result shows here when it finishes`);
      setOpen(false); setOk(false); reload();
    } catch (e) { toast.error(errMsg(e, "Could not start the live test")); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2" data-testid="uc-live-test">
      <div className="text-[11px] font-semibold text-slate-700">Step 1 · Live test on one domain</div>
      {t ? <LastTest t={t} /> : <p className="mt-0.5 text-[11px] text-slate-500">Write one domain's amount to Stax Bill and check it there before switching on.</p>}
      <Button size="sm" variant="outline" className="mt-2" onClick={() => setOpen(true)} disabled={!!ov.running} data-testid="uc-live-test-open">
        <FlaskConical className="h-3.5 w-3.5" /> {t ? "Run another live test…" : "Live test one domain…"}</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="uc-live-test-dialog">
          <AlertDialogHeader><AlertDialogTitle>Write one domain to Stax Bill</AlertDialogTitle>
            <AlertDialogDescription>Only this domain's subscription is changed: its Long Distance and Toll Free Usage product gets the open cycle's amount, included, quantity 1. Everything else stays a dry run.</AlertDialogDescription></AlertDialogHeader>
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Domain</span>
            <Input list="uc-live-test-domains" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. acme.ca" className="h-9 font-mono text-sm" data-testid="uc-live-test-domain" />
            <datalist id="uc-live-test-domains">{choices.map((r) => <option key={r.domain} value={r.domain}>{`${money(r.amount)}${r.customer ? ` · ${r.customer}` : ""}`}</option>)}</datalist>
            <span className="block text-[11px] text-slate-500">{choices.length ? `${choices.length} domains in the latest dry run would change — start typing to pick one.` : "Pick a domain the latest dry run marked “Would change”."}</span></label>
          <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="uc-live-test-confirm" />
            I want this one live subscription changed now.</label>
          <AlertDialogFooter><AlertDialogCancel data-testid="uc-live-test-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); start(); }} disabled={!ok || busy || domain.trim().length < 3} data-testid="uc-live-test-start">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Write this domain</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
